import { useForm } from "react-hook-form";

import { Form } from "@/components/Form/Form";
import { FormHiddenInput } from "@/components/Form/FormHiddenInput";
import { FormInput } from "@/components/Form/FormInput";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { createRequiredStringRule } from "@/helpers/rules";
import { useTranslation } from "@/translations";

const FORM_ID = "maintainer-modal-form";

type CustomUrlModalProps = ModalBaseProps & {
  isLoading: boolean;
  onSubmit: (id: string | undefined, maintainer: string) => Promise<void>;
  defaultValues?: FormValues;
};

interface FormValues {
  id: string;
  maintainer: string;
}

export function MaintainerModal({
  isOpened,
  onOpenChange,
  isLoading,
  onSubmit,
  defaultValues,
}: CustomUrlModalProps): React.ReactNode {
  const { t } = useTranslation();
  const form = useForm<FormValues>({
    defaultValues: defaultValues,
  });

  async function handleSubmit() {
    const formValues = form.getValues();
    form.reset();
    await onSubmit(formValues.id, formValues.maintainer);

    onOpenChange(false);
  }

  return (
    <Modal.Root title={t("page.plus-button-links.maintainer.modal.title")} size="sm" {...{ onOpenChange, isOpened }}>
      <Modal.Body>
        <Form.Root id={FORM_ID} formMethods={form} onSubmit={handleSubmit}>
          <Form.Body>
            <FormHiddenInput<FormValues> name="id" />
            <FormField label={t("page.plus-button-links.maintainer.modal.input-name")} required>
              <FormInput<FormValues>
                name="maintainer"
                placeholder={t("page.plus-button-links.maintainer.modal.input-placeholder")}
                rules={{
                  validate: {
                    required: createRequiredStringRule(t, "page.plus-button-links.maintainer.modal.input-name"),
                  },
                }}
              />
            </FormField>
          </Form.Body>
        </Form.Root>
      </Modal.Body>
      <Modal.Actions>
        <Modal.Close>
          <Button variant="outline" tone="primary">
            {t("common.action.cancel")}
          </Button>
        </Modal.Close>
        <Button type="submit" variant="filled" form={FORM_ID} isLoading={isLoading}>
          {t("common.action.save")}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}
